import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlignJustify, ArrowRight, Search, X } from "lucide-react";
import Slider from "./components/Slider";
import DotGrid from "./components/DotGrid";
import Dropdown from "./components/Dropdown";
import FilterSidebar from "./components/FilterSidebar";
import { UserLayout } from "./components/layout/UserLayout";
import PageHero from "./components/PageHero";
import Container from "./components/Container";
import ProductCard from "./components/cards/ProductCard";
import ImageWithBody from "./components/img/ImageWithBody";
import Text from "./components/Text";
import { getImg } from "../../Components/Utils";
import { ROUTE_PATHS } from "../../constants/routeConstants";
import {
  useFetchAllProductsQuery,
  useFetchFrontendCategoriesQuery,
  useFetchFrontendBrandsQuery,
  useFetchFrontendSubCategoriesQuery,
} from "../../stores/api/pages.api";

const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "most-viewed", label: "Most Viewed" },
];

const toSortParams = (sort) => {
  if (sort === "most-viewed") return { sortBy: "views", sortOrder: "DESC" };
  return {};
};

const GRID_OPTIONS = [
  { cols: 2, icon: <DotGrid cols={2} />, gridClass: "grid-cols-2" },
  { cols: 3, icon: <DotGrid cols={3} />, gridClass: "grid-cols-2 sm:grid-cols-3" },
  { cols: 4, icon: <DotGrid cols={4} />, gridClass: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" },
  { cols: 5, icon: <DotGrid cols={5} />, gridClass: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" },
  { cols: 6, icon: <DotGrid cols={6} />, gridClass: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" },
];

const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const ProductsSkeleton = () => (
  <UserLayout>
    <Container variant="narrow" className="pt-10 px-4 lg:px-8">
      <Skeleton className="h-6 w-48 mb-6" />
      <div className="grid grid-cols-5 gap-4 mb-10">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-[330px]" />
        ))}
      </div>
    </Container>
    <Container variant="narrow" className="py-10 px-4 lg:px-8">
      <Skeleton className="h-6 w-32 mb-6" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <Skeleton key={i} className="h-[280px]" />
        ))}
      </div>
    </Container>
  </UserLayout>
);

const Products = () => {
  const [searchParams] = useSearchParams();
  const brand_id = searchParams.get("brand_id") ?? undefined;
  const category_id = searchParams.get("category_id") ?? undefined;
  const sub_category_id = searchParams.get("sub_category_id") ?? undefined;

  const [cols, setCols] = useState(4);
  const [sort, setSort] = useState("featured");
  const [filterOpen, setFilterOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data: productsData, isLoading: productsLoading } = useFetchAllProductsQuery(
    { brand_id, category_id, sub_category_id, search, ...toSortParams(sort) },
  );
  const { data: categoriesData, isLoading: categoriesLoading } = useFetchFrontendCategoriesQuery();
  const { data: brandsData } = useFetchFrontendBrandsQuery();
  const { data: subCategoriesData } = useFetchFrontendSubCategoriesQuery();

  const isLoading = productsLoading || categoriesLoading;
  if (isLoading) return <ProductsSkeleton />;

  const categories = (categoriesData ?? []).map((c) => ({ label: c.name, img: c.image, slug: c.slug }));
  const filterCategories = (categoriesData ?? []).map((c) => ({ id: c.id, name: c.name }));
  const filterBrands = (brandsData ?? []).map((b) => ({ id: b.id, name: b.name }));
  const filterSubCategories = (subCategoriesData ?? []).map((s) => ({
    id: s.id,
    name: s.name,
    category: s.category ? { name: s.category.name } : null,
  }));
  const products = (productsData?.data ?? []).map((p) => ({
    ...p,
    title: p.name,
    img: p.image,
    hoverImg: p.image??p.images?.[0]?.image,
  }));
console.log(products);
  const gridClass =
    GRID_OPTIONS.find((g) => g.cols === cols)?.gridClass ?? "grid-cols-4";

  return (
    <UserLayout>
      <PageHero
        title="All Products"
        crumbs={[
          { label: "Home", href: ROUTE_PATHS.HOME },
          { label: "Products" },
        ]}
      />

      {/* Category slider */}
      {categories.length > 0 && (
        <Container variant="narrow" className="pt-10 px-4 lg:px-8">
          <Text is="h2" className="text-xl font-bold text-gray-900 mb-6">
            Shop by Category
          </Text>
          <Slider
            className="category-slider"
            options={{
              type: "loop",
              perPage: 5,
              perMove: 1,
              gap: "1rem",
              autoplay: true,
              pagination: false,
              arrows: true,
              breakpoints: { 1024: { perPage: 4 }, 640: { perPage: 1 } },
            }}
          >
            {({ SplideSlide }) =>
              categories.map((cat) => (
                <SplideSlide key={cat.label}>
                  <ImageWithBody
                    src={getImg(cat.img)}
                    alt={cat.label}
                    className="min-h-[330px] flex justify-center items-end rounded-xl"
                  >
                    <Text
                      is="a"
                      href={ROUTE_PATHS.CATEGORY_DETAIL.replace(":categorySlug", cat.slug)}
                      className="group mb-10 flex items-center gap-1 bg-white text-gray-900 text-sm font-medium px-5 py-2.5 shadow-sm hover:shadow-md transition-shadow whitespace-nowrap rounded-lg"
                    >
                      <Text is="span" variant="body" className="text-gray-900">
                        {cat.label}
                      </Text>
                      <Text
                        is="span"
                        className="inline-flex w-0 overflow-hidden opacity-0 translate-y-1.5 group-hover:w-5 group-hover:opacity-100 group-hover:translate-y-0 transition-[width,opacity,transform] duration-300 text-gray-900"
                      >
                        <ArrowRight className="-rotate-45" size={14} />
                      </Text>
                    </Text>
                  </ImageWithBody>
                </SplideSlide>
              ))
            }
          </Slider>
        </Container>
      )}

      <Container variant="narrow" className="py-10 px-4 lg:px-8">
        <Text is="h2" className="text-xl font-bold text-gray-900 mb-6">
          Products
        </Text>

        {/* Toolbar */}
        <div className="flex items-center mb-8 border border-gray-200 rounded-lg divide-x divide-gray-200">
          {/* Filter */}
          <button
            onClick={() => setFilterOpen(true)}
            className="flex items-center gap-2 px-3 sm:px-5 py-3 text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors flex-shrink-0"
          >
            <AlignJustify size={14} />
            <span className="hidden sm:inline">FILTER</span>
          </button>

          {/* Search */}
          <div className="flex-1 flex items-center gap-2 px-4 py-3">
            <Search size={14} className="text-gray-400 flex-shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products…"
              className="w-full text-sm text-gray-900 placeholder-gray-400 outline-none bg-transparent"
            />
            {search && (
              <button onClick={() => setSearch("")} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                <X size={14} />
              </button>
            )}
          </div>

          {/* Grid icons */}
          <div className="hidden sm:flex items-center gap-0.5 px-3 py-3 flex-shrink-0">
            {GRID_OPTIONS.map((g) => (
              <button
                key={g.cols}
                onClick={() => setCols(g.cols)}
                className={`w-7 h-7 flex items-center justify-center transition-colors ${cols === g.cols ? "text-gray-900" : "text-gray-300 hover:text-gray-600"}`}
                aria-label={`${g.cols} column grid`}
              >
                {g.icon}
              </button>
            ))}
          </div>

          {/* Sort */}
          <div className="flex-shrink-0">
            <Dropdown options={SORT_OPTIONS} value={sort} onChange={setSort} />
          </div>
        </div>

        <FilterSidebar
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          brands={filterBrands}
          categories={filterCategories}
          subCategories={filterSubCategories}
        />

        {/* Product grid */}
        <Container
          variant="full"
          showEmptyState={!products.length}
          emptyMessage="No products found"
        >
          <div className={`grid gap-4 ${gridClass}`}>
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </Container>
      </Container>
    </UserLayout>
  );
};

export default Products;
